import { useState } from 'react'

import { useTagDashboardApi } from '@/common/api/modules/searchTag'
import { MeasurementStatus, TagFunnelStep } from '@/common/api/params/searchTag'
import { searchTagCandidatesPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

const PERIODS = [7, 30, 90] as const

/**
 * 指標の表示。null は「未計測」であって 0% ではない。
 * 0% を出すと「効果が無かった」と読まれ、設置漏れに気づけなくなる。
 */
const Metric = ({
  label,
  value,
  unit = '',
  hint
}: {
  label: string
  value: number | null
  unit?: string
  hint?: string
}) => (
  <div className='border-border-input bg-surface flex flex-col gap-1 rounded-xl border p-4'>
    <Text size={12} color={TextColor.SECONDARY}>
      {label}
    </Text>
    {value === null ? (
      <Text size={20} color={TextColor.SECONDARY}>
        未計測
      </Text>
    ) : (
      <Text size={24} weight='bold'>
        {unit === '¥' ? `¥${value.toLocaleString()}` : `${value.toLocaleString()}${unit}`}
      </Text>
    )}
    {hint && (
      <Text size={10} color={TextColor.SECONDARY}>
        {hint}
      </Text>
    )}
  </div>
)

const STATUS_TEXT: Record<MeasurementStatus, string> = {
  active: '計測中',
  stale: '計測が止まっています',
  no_data: '計測未接続'
}

const FunnelRow = ({ step }: { step: TagFunnelStep }) => (
  <div className='border-border-input flex items-center gap-3 border-b py-2 last:border-b-0'>
    <div className='w-40'>
      <Text size={13}>{step.label}</Text>
    </div>
    <div className='w-24 text-right'>
      {step.count === null ? (
        <Text color={TextColor.SECONDARY}>—</Text>
      ) : (
        <Text size={14} weight='bold'>
          {step.count.toLocaleString()}
        </Text>
      )}
    </div>
    <div className='w-24 text-right'>
      {step.rate_from_prev === null ? (
        <Text color={TextColor.SECONDARY}>—</Text>
      ) : (
        <Text size={12} color={TextColor.SECONDARY}>
          {step.rate_from_prev}%
        </Text>
      )}
    </div>
  </div>
)

const SearchTagDashboardPage = () => {
  const siteId = useSiteId()
  const [days, setDays] = useState<number>(30)
  const { data, isLoading } = useTagDashboardApi({ site_id: siteId, days })

  const k = data?.kpis
  const notMeasured = k && k.measurement_status !== 'active'

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='ダッシュボード'
        description='タグ表示からタグページ・購入までの回遊と成果を確認します。'
        rightNode={
          <div className='flex items-center gap-1'>
            {PERIODS.map((d) => (
              <Button
                key={d}
                color={days === d ? ButtonColor.PRIMARY : ButtonColor.SECONDARY}
                onClick={() => setDays(d)}
              >
                過去{d}日
              </Button>
            ))}
          </div>
        }
      />
      <Content>
        {isLoading || !data || !k ? (
          <Text color={TextColor.SECONDARY}>読み込み中...</Text>
        ) : (
          <>
            {notMeasured && (
              <div className='bg-surface-warning border-border-input rounded-xl border p-4'>
                <Text size={14} weight='bold'>
                  {STATUS_TEXT[k.measurement_status]}
                </Text>
                <div className='mt-1'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    {k.measurement_status === 'no_data'
                      ? '設置以来、計測イベントを1件も受信していません。w2Commerce 側にタグの表示コードと計測タグ（w2search.js）の両方が設置されているかご確認ください。'
                      : `最終受信: ${k.last_event_at ? new Date(k.last_event_at).toLocaleString('ja-JP') : '不明'}。設置が外れた可能性があります。`}
                  </Text>
                </div>
                <div className='mt-1'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    数値が出ていない指標は「未計測」と表示しています（0% ではありません）。
                  </Text>
                </div>
              </div>
            )}

            <div className='grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6'>
              <Metric
                label='タグ表示回数'
                value={k.tag_impressions}
                hint={`うち0件表示 ${k.tag_frame_views} 回`}
              />
              <Metric
                label='タグCTR'
                value={k.tag_ctr}
                unit='%'
                hint={`クリック ${k.tag_clicks} 回`}
              />
              <Metric label='タグページ表示' value={k.tag_page_views} />
              <Metric
                label='タグページCTR'
                value={k.tag_page_ctr}
                unit='%'
                hint={`商品クリック ${k.product_clicks} 回`}
              />
              <Metric
                label='タグ経由CV'
                value={k.cv_count}
                hint={k.cvr === null ? '母数なし' : `CVR ${k.cvr}%（母数 ${k.cvr_denominator}）`}
              />
              <Metric label='タグ経由GMV' value={k.gmv} unit='¥' />
            </div>

            <Area>
              <Heading>回遊ファネル</Heading>
              <AreaContent>
                <div className='border-border-input flex items-center gap-3 border-b py-1'>
                  <div className='w-40'>
                    <Text size={10} color={TextColor.SECONDARY}>
                      段階
                    </Text>
                  </div>
                  <div className='w-24 text-right'>
                    <Text size={10} color={TextColor.SECONDARY}>
                      件数
                    </Text>
                  </div>
                  <div className='w-24 text-right'>
                    <Text size={10} color={TextColor.SECONDARY}>
                      前段比
                    </Text>
                  </div>
                </div>
                {data.funnel.map((s) => (
                  <FunnelRow key={s.key} step={s} />
                ))}
              </AreaContent>
            </Area>

            <Area>
              <Heading>タグ別の成果</Heading>
              <AreaContent>
                {data.tags.length === 0 ? (
                  <div className='flex flex-col items-start gap-2'>
                    <Text color={TextColor.SECONDARY}>
                      集計対象のタグがありません。承認キューでタグを承認すると、翌日の集計から表示されます。
                    </Text>
                    <Button color={ButtonColor.SECONDARY} link={searchTagCandidatesPath(siteId)}>
                      承認キューを開く
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <thead>
                      <TableRow head>
                        <TableHeadItem>
                          <Text size={12}>タグ</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text size={12}>表示</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text size={12}>クリック</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text size={12}>CTR</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text size={12}>ページ表示</Text>
                        </TableHeadItem>
                        <TableHeadItem>
                          <Text size={12}>CV</Text>
                        </TableHeadItem>
                      </TableRow>
                    </thead>
                    <tbody>
                      {data.tags.map((t) => (
                        <TableRow key={t.tag_id}>
                          <TableRowItem>{t.tag_name}</TableRowItem>
                          <TableRowItem>{t.impressions.toLocaleString()}</TableRowItem>
                          <TableRowItem>{t.clicks.toLocaleString()}</TableRowItem>
                          <TableRowItem>
                            {t.ctr === null ? (
                              <Text color={TextColor.SECONDARY}>—</Text>
                            ) : (
                              `${t.ctr}%`
                            )}
                          </TableRowItem>
                          <TableRowItem>{t.page_views.toLocaleString()}</TableRowItem>
                          <TableRowItem>{t.conversions.toLocaleString()}</TableRowItem>
                        </TableRow>
                      ))}
                    </tbody>
                  </Table>
                )}
              </AreaContent>
            </Area>
          </>
        )}
      </Content>
    </Main>
  )
}

export default SearchTagDashboardPage
